import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle, Check, CheckCircle2, CheckSquare, Eye, EyeOff,
  Image as ImageIcon, Loader2, Pencil, Plus, Save, Square, Star,
  Trash2, UploadCloud, X,
} from "lucide-react";

import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useBulkDeleteGalleryImages, useBulkUpdateGalleryImages,
  useCreateGalleryImage, useDeleteGalleryImage, useGalleryImages,
  useUpdateGalleryImage, type GalleryImageRow,
} from "@/hooks/useGallery";
import { cn } from "@/lib/utils";

/**
 * /gallery — Admin-only management surface for the public photo gallery
 * (rendered server-side at /gallery/ on the public site). Lets an admin
 * upload, rename, feature, hide, and delete tiles without bouncing to
 * /admin/. Backed by /api/v1/gallery-images/ (locked to Admin role).
 *
 * Layout: header + upload button on top, then a responsive grid of
 * tile previews with inline action buttons. Tile state (featured /
 * active) shows as badges on the image; click the star or eye icons to
 * toggle without opening an editor.
 */
export default function Gallery() {
  const list       = useGalleryImages();
  const create     = useCreateGalleryImage();
  const update     = useUpdateGalleryImage();
  const del        = useDeleteGalleryImage();
  const bulkUpdate = useBulkUpdateGalleryImages();
  const bulkDelete = useBulkDeleteGalleryImages();
  const confirm    = useConfirm();

  const [filter, setFilter] = useState<"all" | "active" | "hidden">("all");
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState<GalleryImageRow | null>(null);

  // ---- Selection ----------------------------------------------------------
  // Set<number> of GalleryImage IDs currently ticked. Cleared whenever a
  // bulk action completes or the user hits Esc / Clear.
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const bulkBusy = bulkUpdate.isPending || bulkDelete.isPending;

  function toggleSelect(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function clearSelection() { setSelected(new Set()); }

  const rows = list.data ?? [];
  const visible = useMemo(() => {
    if (filter === "active") return rows.filter((r) => r.is_active);
    if (filter === "hidden") return rows.filter((r) => !r.is_active);
    return rows;
  }, [rows, filter]);

  // Selecting all = the IDs of what's currently *visible* under the filter
  // (so "Select all" while on the Hidden tab only grabs hidden rows).
  const visibleIds = useMemo(() => new Set(visible.map((r) => r.id)), [visible]);
  const allVisibleSelected =
    visible.length > 0 && visible.every((r) => selected.has(r.id));
  function selectAllVisible() {
    setSelected(new Set([...selected, ...visibleIds]));
  }

  // Drop selected ids that no longer exist (e.g. after a delete or after
  // the filter switches and the row falls out of the visible set).
  useEffect(() => {
    if (selected.size === 0) return;
    const existing = new Set(rows.map((r) => r.id));
    const trimmed = new Set([...selected].filter((id) => existing.has(id)));
    if (trimmed.size !== selected.size) setSelected(trimmed);
  }, [rows, selected]);

  // Esc clears the selection — common bulk-action affordance.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && selected.size > 0 && !bulkBusy) clearSelection();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected.size, bulkBusy]);

  const stats = useMemo(() => ({
    total:    rows.length,
    featured: rows.filter((r) => r.is_featured).length,
    hidden:   rows.filter((r) => !r.is_active).length,
  }), [rows]);

  async function onDelete(row: GalleryImageRow) {
    const ok = await confirm({
      title: "Delete gallery photo?",
      description:
        `"${row.title || "Untitled"}" will be removed from the public ` +
        "gallery immediately. This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(row.id);
  }

  // ---- Bulk handlers ------------------------------------------------------
  async function onBulkPatch(patch: Partial<{ is_active: boolean; is_featured: boolean }>) {
    if (selected.size === 0) return;
    await bulkUpdate.mutateAsync({ ids: [...selected], patch });
    clearSelection();
  }
  async function onBulkDelete() {
    if (selected.size === 0) return;
    const ok = await confirm({
      title: `Delete ${selected.size} gallery photo${selected.size === 1 ? "" : "s"}?`,
      description:
        "Selected photos will be removed from the public gallery " +
        "immediately. This cannot be undone.",
      confirmLabel: `Delete ${selected.size}`,
      destructive: true,
    });
    if (!ok) return;
    await bulkDelete.mutateAsync([...selected]);
    clearSelection();
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* ---------- Header ---------- */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            <ImageIcon className="h-3.5 w-3.5" />Public gallery
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">Gallery</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Curated photos for the public site at{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">/gallery/</code>.
            Upload images here and they appear immediately. Featured tiles
            float to the top.
          </p>
        </div>
        <Button onClick={() => setShowUpload(true)} className="shrink-0">
          <Plus className="mr-2 h-4 w-4" />Upload photos
        </Button>
      </header>

      {/* ---------- Stat strip + filter ---------- */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <StatPill label="Total" value={stats.total} />
        <StatPill label="Featured" value={stats.featured} accent="text-amber-600" />
        <StatPill label="Hidden" value={stats.hidden} accent="text-muted-foreground" />
        <div className="ml-auto flex items-center gap-1 rounded-md border border-border/60 bg-card p-1">
          <FilterChip current={filter} value="all"    onClick={setFilter}>All</FilterChip>
          <FilterChip current={filter} value="active" onClick={setFilter}>Active</FilterChip>
          <FilterChip current={filter} value="hidden" onClick={setFilter}>Hidden</FilterChip>
        </div>
      </div>

      {/* ---------- Bulk action bar (sticky when any are selected) ---------- */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-30 -mx-2 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 shadow-soft backdrop-blur supports-[backdrop-filter]:bg-primary/10">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <CheckSquare className="h-4 w-4" />
            {selected.size} selected
          </div>
          <Button variant="ghost" size="sm" onClick={selectAllVisible} disabled={bulkBusy || allVisibleSelected} className="h-7">
            Select all visible
          </Button>
          <div className="mx-1 hidden h-5 w-px bg-border/60 sm:block" />
          <Button variant="outline" size="sm" onClick={() => onBulkPatch({ is_active: true })}  disabled={bulkBusy} className="h-7">
            <Eye className="mr-1.5 h-3.5 w-3.5" />Show
          </Button>
          <Button variant="outline" size="sm" onClick={() => onBulkPatch({ is_active: false })} disabled={bulkBusy} className="h-7">
            <EyeOff className="mr-1.5 h-3.5 w-3.5" />Hide
          </Button>
          <Button variant="outline" size="sm" onClick={() => onBulkPatch({ is_featured: true })}  disabled={bulkBusy} className="h-7">
            <Star className="mr-1.5 h-3.5 w-3.5" />Feature
          </Button>
          <Button variant="outline" size="sm" onClick={() => onBulkPatch({ is_featured: false })} disabled={bulkBusy} className="h-7">
            <Star className="mr-1.5 h-3.5 w-3.5 opacity-40" />Unfeature
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onBulkDelete}
            disabled={bulkBusy}
            className="h-7 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete
          </Button>
          <Button variant="ghost" size="sm" onClick={clearSelection} disabled={bulkBusy} className="ml-auto h-7">
            {bulkBusy
              ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Working…</>
              : <><X className="mr-1.5 h-3.5 w-3.5" />Clear</>}
          </Button>
        </div>
      )}

      {/* ---------- Grid ---------- */}
      {list.isLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[4/3] w-full rounded-xl" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <ImageIcon className="h-5 w-5 text-muted-foreground" />
            </div>
            <h3 className="text-sm font-semibold">
              {filter === "all" ? "No photos yet" : `No ${filter} photos`}
            </h3>
            <p className="max-w-sm text-xs text-muted-foreground">
              {filter === "all"
                ? "Upload your first photos to populate the public gallery. The page will start displaying them instantly."
                : "Switch the filter back to All, or upload more photos."}
            </p>
            {filter === "all" && (
              <Button onClick={() => setShowUpload(true)} className="mt-2">
                <Plus className="mr-2 h-4 w-4" />Upload photos
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {visible.map((row) => (
            <GalleryTile
              key={row.id}
              row={row}
              isSelected={selected.has(row.id)}
              anySelected={selected.size > 0}
              onToggleSelect={() => toggleSelect(row.id)}
              onEdit={() => setEditing(row)}
              onDelete={() => onDelete(row)}
              onToggleFeatured={() => update.mutate({ id: row.id, is_featured: !row.is_featured })}
              onToggleActive={() => update.mutate({ id: row.id, is_active: !row.is_active })}
              busy={update.isPending || del.isPending || bulkBusy}
            />
          ))}
        </div>
      )}

      {/* ---------- Dialogs ---------- */}
      <UploadDialog
        open={showUpload}
        onOpenChange={setShowUpload}
        uploadOne={async (input) => { await create.mutateAsync(input); }}
      />
      <EditDialog
        row={editing}
        onClose={() => setEditing(null)}
        onSubmit={(input) => update.mutate(
          { id: editing!.id, ...input },
          { onSuccess: () => setEditing(null) },
        )}
        busy={update.isPending}
        error={extractError(update.error)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat pill + filter chip
// ---------------------------------------------------------------------------
function StatPill({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-2.5 py-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-semibold tabular-nums", accent ?? "text-foreground")}>{value}</span>
    </span>
  );
}

function FilterChip<T extends string>({
  current, value, onClick, children,
}: {
  current: T;
  value: T;
  onClick: (v: T) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={cn(
        "rounded px-2.5 py-1 text-xs font-medium transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Tile
// ---------------------------------------------------------------------------
function GalleryTile({
  row, isSelected, anySelected, onToggleSelect,
  onEdit, onDelete, onToggleFeatured, onToggleActive, busy,
}: {
  row: GalleryImageRow;
  isSelected: boolean;
  /** True if ANY tile in the grid is selected — used to keep the
   *  checkbox always-visible (not just hover) once selection starts. */
  anySelected: boolean;
  onToggleSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleFeatured: () => void;
  onToggleActive: () => void;
  busy: boolean;
}) {
  return (
    <Card className={cn(
      "group relative overflow-hidden border-border/60 transition-all hover:shadow-md",
      !row.is_active && "opacity-60",
      isSelected && "ring-2 ring-primary ring-offset-2 ring-offset-background",
    )}>
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        {row.image_url ? (
          <img
            src={row.image_url}
            alt={row.title || "Gallery photo"}
            loading="lazy"
            className={cn(
              "h-full w-full object-cover transition-transform duration-500",
              !isSelected && "group-hover:scale-105",
            )}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <ImageIcon className="h-8 w-8" />
          </div>
        )}

        {/* Top-left badges */}
        <div className="absolute left-2 top-2 flex flex-wrap gap-1">
          {row.is_featured && (
            <Badge className="border-0 bg-amber-400 text-amber-950 hover:bg-amber-400">
              <Star className="mr-1 h-3 w-3 fill-current" />Featured
            </Badge>
          )}
          {!row.is_active && (
            <Badge variant="outline" className="border-foreground/30 bg-background/80 text-foreground">
              Hidden
            </Badge>
          )}
        </div>

        {/* Top-right action cluster. Always reveals on hover; the
            checkbox additionally sticks visible whenever ANY tile in
            the grid is selected (so the selection gesture is obvious
            without having to hover). */}
        <div className={cn(
          "absolute right-2 top-2 flex gap-1 transition-opacity",
          // Whole cluster reveals on hover or while in selection mode.
          anySelected ? "opacity-100" : "opacity-0 group-hover:opacity-100",
        )}>
          {/* Select checkbox — different visual from the rest because
              it's a stateful toggle, not a one-shot action. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
            aria-label={isSelected ? "Deselect photo" : "Select photo"}
            aria-pressed={isSelected}
            disabled={busy}
            title={isSelected ? "Deselect" : "Select"}
            className={cn(
              "flex h-7 w-7 items-center justify-center rounded-md border transition-colors",
              isSelected
                ? "border-primary bg-primary text-primary-foreground"
                : "border-white/20 bg-background/85 text-foreground backdrop-blur-sm hover:bg-background",
              busy && "cursor-not-allowed opacity-50",
            )}
          >
            {isSelected
              ? <Check  className="h-3.5 w-3.5" />
              : <Square className="h-3.5 w-3.5 stroke-[1.6]" />}
          </button>
          <IconButton
            onClick={onToggleFeatured}
            title={row.is_featured ? "Unfeature" : "Feature"}
            disabled={busy}
          >
            <Star className={cn("h-3.5 w-3.5", row.is_featured && "fill-current text-amber-500")} />
          </IconButton>
          <IconButton
            onClick={onToggleActive}
            title={row.is_active ? "Hide from public site" : "Show on public site"}
            disabled={busy}
          >
            {row.is_active
              ? <Eye    className="h-3.5 w-3.5" />
              : <EyeOff className="h-3.5 w-3.5" />}
          </IconButton>
          <IconButton onClick={onEdit} title="Edit details" disabled={busy}>
            <Pencil className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton
            onClick={onDelete}
            title="Delete"
            disabled={busy}
            variant="destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      </div>

      <CardContent className="p-3">
        <div className="truncate text-sm font-medium">
          {row.title || <span className="italic text-muted-foreground">Untitled</span>}
        </div>
        {row.caption && (
          <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            {row.caption}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function IconButton({
  children, onClick, title, disabled, variant,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  disabled?: boolean;
  variant?: "destructive";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md border border-white/20 backdrop-blur-sm transition-colors",
        variant === "destructive"
          ? "bg-rose-500/85 text-white hover:bg-rose-600"
          : "bg-background/85 text-foreground hover:bg-background",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Upload dialog — multi-file
// ---------------------------------------------------------------------------
type QueueStatus = "pending" | "uploading" | "done" | "error";

interface QueueItem {
  /** Stable local id (not the server id). */
  uid: string;
  file: File;
  preview: string;
  title: string;
  caption: string;
  status: QueueStatus;
  error?: string;
}

/** Strip extension, replace separators with spaces, title-case so the
 *  filename becomes a usable default title. "green_legacy-2026.jpg"
 *  -> "Green Legacy 2026". */
function deriveTitle(filename: string): string {
  const stem = filename.replace(/\.[^.]+$/, "");
  const words = stem.replace(/[-_]+/g, " ").trim();
  return words
    .split(/\s+/)
    .map((w) => w ? w[0].toUpperCase() + w.slice(1) : w)
    .join(" ");
}

function UploadDialog({
  open, onOpenChange, uploadOne,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Uploads a single photo. Resolves on success, throws on failure
   *  (the dialog catches and tags the queue item). */
  uploadOne: (input: {
    image: File; title: string; caption: string;
    is_featured: boolean; is_active: boolean;
  }) => Promise<void>;
}) {
  const [items, setItems]       = useState<QueueItem[]>([]);
  const [featured, setFeatured] = useState(false);
  const [busy, setBusy]         = useState(false);
  const fileInputRef            = useRef<HTMLInputElement | null>(null);

  // Reset whenever the dialog reopens
  useEffect(() => {
    if (open) {
      setItems([]);
      setFeatured(false);
      setBusy(false);
    }
  }, [open]);

  // Revoke object URLs on unmount / item removal so we don't leak.
  useEffect(() => {
    return () => {
      items.forEach((it) => URL.revokeObjectURL(it.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addFiles(picked: FileList | File[] | null) {
    if (!picked || !picked.length) return;
    const next: QueueItem[] = Array.from(picked)
      .filter((f) => f.type.startsWith("image/"))
      .map((f) => ({
        uid: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        preview: URL.createObjectURL(f),
        title: deriveTitle(f.name),
        caption: "",
        status: "pending" as QueueStatus,
      }));
    if (!next.length) return;
    setItems((prev) => [...prev, ...next]);
  }

  function removeItem(uid: string) {
    setItems((prev) => {
      const gone = prev.find((it) => it.uid === uid);
      if (gone) URL.revokeObjectURL(gone.preview);
      return prev.filter((it) => it.uid !== uid);
    });
  }

  function patchItem(uid: string, patch: Partial<QueueItem>) {
    setItems((prev) => prev.map((it) => it.uid === uid ? { ...it, ...patch } : it));
  }

  /** Sequential upload — order matters for `sort_order` later, and
   *  the API doesn't need the parallelism. Each item flips status
   *  on its own so the user sees real-time progress. */
  async function handleUpload() {
    if (!items.length || busy) return;
    setBusy(true);
    // Use a local snapshot so we operate on items that exist *now*.
    const pending = items.filter((it) => it.status === "pending" || it.status === "error");
    for (const it of pending) {
      patchItem(it.uid, { status: "uploading", error: undefined });
      try {
        await uploadOne({
          image: it.file,
          title: it.title,
          caption: it.caption,
          is_featured: featured,
          // New uploads land visible by default. The model default is
          // already True but we send it explicitly so the answer doesn't
          // depend on DRF's multipart-vs-default behaviour.
          is_active: true,
        });
        patchItem(it.uid, { status: "done" });
      } catch (err: unknown) {
        patchItem(it.uid, {
          status: "error",
          error: extractError(err) || "Upload failed.",
        });
      }
    }
    setBusy(false);
  }

  const allDone   = items.length > 0 && items.every((it) => it.status === "done");
  const doneCount = items.filter((it) => it.status === "done").length;
  const errCount  = items.filter((it) => it.status === "error").length;

  function handleClose() {
    if (busy) return;
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload photos</DialogTitle>
          <DialogDescription>
            Pick one or many images. Titles auto-fill from each filename —
            edit before uploading if needed. JPG / PNG / WebP, up to 10 MB each.
          </DialogDescription>
        </DialogHeader>

        {/* --- Pick / drop zone OR file queue --- */}
        {items.length === 0 ? (
          <DropZone onPick={addFiles} disabled={busy} />
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-muted-foreground">
                {items.length} photo{items.length === 1 ? "" : "s"} in queue
                {busy && <> · uploading <strong>{doneCount}/{items.length}</strong></>}
                {!busy && allDone && <> · all uploaded</>}
                {!busy && errCount > 0 && <> · <span className="text-destructive">{errCount} failed</span></>}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />Add more
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="sr-only"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              />
            </div>

            <div className="max-h-[55vh] space-y-2 overflow-y-auto rounded-lg border border-border/60 bg-muted/30 p-2">
              {items.map((it) => (
                <QueueRow
                  key={it.uid}
                  item={it}
                  busy={busy}
                  onTitleChange={(v) => patchItem(it.uid, { title: v })}
                  onCaptionChange={(v) => patchItem(it.uid, { caption: v })}
                  onRemove={() => removeItem(it.uid)}
                />
              ))}
            </div>
          </div>
        )}

        {/* --- Shared options + actions --- */}
        {items.length > 0 && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={featured}
              onChange={(e) => setFeatured(e.target.checked)}
              disabled={busy || allDone}
              className="h-4 w-4 rounded border-border/60 text-primary focus:ring-2 focus:ring-primary/30"
            />
            <span>Mark <strong>all</strong> as <strong>Featured</strong> (pinned to top of grid)</span>
          </label>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={handleClose} disabled={busy}>
            {allDone ? "Close" : "Cancel"}
          </Button>
          {!allDone && (
            <Button
              type="button"
              onClick={handleUpload}
              disabled={items.length === 0 || busy}
            >
              {busy
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading {doneCount}/{items.length}…</>
                : <><UploadCloud className="mr-2 h-4 w-4" />
                    Upload {items.length} photo{items.length === 1 ? "" : "s"}
                  </>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DropZone({
  onPick, disabled,
}: {
  onPick: (files: FileList | File[] | null) => void;
  disabled: boolean;
}) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <label
      htmlFor="gallery-file-multi"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onPick(e.dataTransfer.files);
      }}
      className={cn(
        "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border/60 bg-muted/40 px-4 py-12 text-center transition-colors hover:bg-muted/60",
        dragOver && "border-primary bg-primary/5",
        disabled && "pointer-events-none opacity-60",
      )}
    >
      <UploadCloud className="h-10 w-10 text-muted-foreground" />
      <div className="text-sm font-medium">Click or drag images here</div>
      <div className="text-xs text-muted-foreground">JPG / PNG / WebP — multiple files OK</div>
      <input
        id="gallery-file-multi"
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        onChange={(e) => { onPick(e.target.files); e.target.value = ""; }}
        disabled={disabled}
      />
    </label>
  );
}

function QueueRow({
  item, busy, onTitleChange, onCaptionChange, onRemove,
}: {
  item: QueueItem;
  busy: boolean;
  onTitleChange: (v: string) => void;
  onCaptionChange: (v: string) => void;
  onRemove: () => void;
}) {
  const editable = !busy && item.status !== "done";
  return (
    <div className={cn(
      "flex gap-3 rounded-md border border-border/60 bg-card p-2 transition-colors",
      item.status === "done"      && "bg-emerald-50/60 dark:bg-emerald-950/20",
      item.status === "error"     && "bg-rose-50/60 dark:bg-rose-950/20",
      item.status === "uploading" && "bg-primary/5",
    )}>
      <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md bg-muted">
        <img src={item.preview} alt="" className="h-full w-full object-cover" />
        {item.status !== "pending" && (
          <div className="absolute inset-0 flex items-center justify-center bg-foreground/30">
            {item.status === "uploading" && <Loader2 className="h-5 w-5 animate-spin text-white" />}
            {item.status === "done"      && <CheckCircle2 className="h-5 w-5 text-white" />}
            {item.status === "error"     && <AlertCircle  className="h-5 w-5 text-white" />}
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1 space-y-1">
        <Input
          value={item.title}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Title (auto-filled from filename)"
          maxLength={200}
          disabled={!editable}
          className="h-8 text-sm"
        />
        <Input
          value={item.caption}
          onChange={(e) => onCaptionChange(e.target.value)}
          placeholder="Caption (optional)"
          disabled={!editable}
          className="h-8 text-xs"
        />
        {item.error && (
          <div className="truncate text-[11px] text-destructive">
            {item.error}
          </div>
        )}
      </div>

      {item.status !== "done" && (
        <button
          type="button"
          onClick={onRemove}
          disabled={busy && item.status === "uploading"}
          aria-label="Remove from queue"
          className={cn(
            "self-start rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
            busy && item.status === "uploading" && "cursor-not-allowed opacity-40",
          )}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit dialog
// ---------------------------------------------------------------------------
function EditDialog({
  row, onClose, onSubmit, busy, error,
}: {
  row: GalleryImageRow | null;
  onClose: () => void;
  onSubmit: (input: { title: string; caption: string; image?: File; is_featured: boolean; is_active: boolean }) => void;
  busy: boolean;
  error: string | null;
}) {
  const [title, setTitle]       = useState("");
  const [caption, setCaption]   = useState("");
  const [file, setFile]         = useState<File | null>(null);
  const [preview, setPreview]   = useState<string | null>(null);
  const [featured, setFeatured] = useState(false);
  const [active, setActive]     = useState(true);

  useEffect(() => {
    if (row) {
      setTitle(row.title || "");
      setCaption(row.caption || "");
      setFeatured(row.is_featured);
      setActive(row.is_active);
      setFile(null); setPreview(null);
    }
  }, [row]);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({
      title, caption,
      is_featured: featured,
      is_active:   active,
      image: file ?? undefined,
    });
  }

  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit photo</DialogTitle>
          <DialogDescription>
            Update the title or caption. Replace the image only if you
            want to swap the photo itself.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="overflow-hidden rounded-lg border border-border/60">
            <img
              src={preview || row?.image_url || ""}
              alt={row?.title || "Current photo"}
              className="max-h-64 w-full bg-muted object-contain"
            />
          </div>

          <div className="flex items-center justify-between gap-2 rounded-md border border-dashed border-border/60 bg-muted/40 px-3 py-2">
            <div className="text-xs">
              {file
                ? <span className="font-medium">Will replace with: {file.name}</span>
                : <span className="text-muted-foreground">Keep current photo, or pick a replacement.</span>}
            </div>
            <label htmlFor="gallery-file-edit" className="cursor-pointer">
              <span className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-card px-2.5 py-1 text-xs font-medium hover:bg-accent">
                <UploadCloud className="h-3.5 w-3.5" />Replace
              </span>
              <input
                id="gallery-file-edit"
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                disabled={busy}
              />
            </label>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="gallery-edit-title">Title</Label>
            <Input
              id="gallery-edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="gallery-edit-caption">Caption</Label>
            <Textarea
              id="gallery-edit-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={2}
              disabled={busy}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border/60 bg-card px-3 py-2 hover:bg-accent">
              <input
                type="checkbox"
                checked={featured}
                onChange={(e) => setFeatured(e.target.checked)}
                disabled={busy}
                className="h-4 w-4 rounded border-border/60 text-primary focus:ring-2 focus:ring-primary/30"
              />
              <Star className={cn("h-3.5 w-3.5", featured && "fill-amber-500 text-amber-500")} />
              <span>Featured</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border/60 bg-card px-3 py-2 hover:bg-accent">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                disabled={busy}
                className="h-4 w-4 rounded border-border/60 text-primary focus:ring-2 focus:ring-primary/30"
              />
              {active
                ? <Eye    className="h-3.5 w-3.5" />
                : <EyeOff className="h-3.5 w-3.5" />}
              <span>{active ? "Visible" : "Hidden"}</span>
            </label>
          </div>

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
                : <><Save className="mr-2 h-4 w-4" />Save</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Error extraction helper
// ---------------------------------------------------------------------------
function extractError(err: unknown): string | null {
  if (!err) return null;
  const e = err as { response?: { data?: { detail?: string; image?: string[]; title?: string[] } } };
  return (
    e.response?.data?.detail
    || e.response?.data?.image?.[0]
    || e.response?.data?.title?.[0]
    || (err as Error).message
    || "Something went wrong."
  );
}
